import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Activity as ActivityIcon, FolderOpen, Users, FileText, Settings, Upload, LogIn, Trash2, Edit2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const fadeIn = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };

const actionIcons = {
  create: { icon: FolderOpen, bg: 'bg-[#ECFDF5]', color: 'text-[#059669]' },
  update: { icon: Edit2, bg: 'bg-[#EEF2FF]', color: 'text-[#4F46E5]' },
  delete: { icon: Trash2, bg: 'bg-[#FEF2F2]', color: 'text-[#DC2626]' },
  upload: { icon: Upload, bg: 'bg-[#F0E6FF]', color: 'text-[#7B2FBE]' },
  login: { icon: LogIn, bg: 'bg-[#FFFBEB]', color: 'text-[#D97706]' },
  signup: { icon: Users, bg: 'bg-[#ECFDF5]', color: 'text-[#059669]' },
  google_login: { icon: LogIn, bg: 'bg-[#FFFBEB]', color: 'text-[#D97706]' },
};

const entityLabels = {
  user: 'User',
  employee: 'Employee',
  paf: 'PAF',
  document: 'Document',
  settings: 'Settings',
};

export default function ActivityPage() {
  const { apiCall } = useAuth();
  const [activities, setActivities] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const limit = 25;

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall('get', `/audit-logs?page=${page}&limit=${limit}`);
      setActivities(res.data.logs);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [apiCall, page]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div data-testid="activity-page">
      <motion.div {...fadeIn} transition={{ duration: 0.3 }}>
        <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight mb-1">Activity</h1>
        <p className="text-[#64748B] mb-8">Recent actions across your organization</p>
      </motion.div>

      <motion.div {...fadeIn} transition={{ duration: 0.3, delay: 0.05 }}
        className="bg-white rounded-2xl border border-indigo-100/50 shadow-sm overflow-hidden"
      >
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-6 h-6 border-2 border-[#4F46E5] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-16">
            <ActivityIcon className="w-12 h-12 text-[#64748B] mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-[#64748B]">No activity yet</p>
          </div>
        ) : (
          <div className="divide-y divide-black/5">
            {activities.map((act, i) => {
              const iconData = actionIcons[act.action] || { icon: FolderOpen, bg: 'bg-[#F8FAFF]', color: 'text-[#64748B]' };
              const Icon = iconData.icon;
              return (
                <motion.div
                  key={act.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: i * 0.02 }}
                  className="flex items-center gap-4 px-6 py-4 hover:bg-[#F8FAFF]/50 transition-colors"
                  data-testid={`activity-item-${act.id}`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconData.bg}`}>
                    <Icon className={`w-5 h-5 ${iconData.color}`} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#0F172A]">
                      <span className="font-medium">{act.user_name || 'System'}</span>
                      {' '}
                      <span className="text-[#64748B]">
                        {act.action === 'create' && 'created'}
                        {act.action === 'update' && 'updated'}
                        {act.action === 'delete' && 'deleted'}
                        {act.action === 'upload' && 'uploaded'}
                        {act.action === 'login' && 'signed in'}
                        {act.action === 'signup' && 'signed up'}
                        {act.action === 'google_login' && 'signed in with Google'}
                      </span>
                      {' '}
                      {act.action !== 'login' && act.action !== 'signup' && act.action !== 'google_login' && (
                        <span className="text-[#0F172A]">
                          {act.entity_type === 'document' ? 'a document' : `a${act.entity_type === 'employee' ? 'n' : ''} ${entityLabels[act.entity_type] || act.entity_type}`}
                        </span>
                      )}
                    </p>
                    {act.details && (
                      <p className="text-xs text-[#64748B] mt-0.5 truncate">{act.details}</p>
                    )}
                  </div>
                  <span className="text-xs text-[#64748B] flex-shrink-0 whitespace-nowrap">
                    {formatTime(act.timestamp)}
                  </span>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-indigo-100/50">
            <span className="text-sm text-[#64748B]">
              Page {page} of {totalPages} ({total} activities)
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
                className="rounded-lg"
                data-testid="activity-prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
                className="rounded-lg"
                data-testid="activity-next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
